import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { templateAccountRoleAndPrivileges } from '../view/template';
import { DropdownEvents } from '../../../../ui/gzDropdown/types';
import { AccountRoleAndPrivilegesWebComponent } from '../component/AccountRoleAndPrivilegesWebComponent';
import { AccountRoleAndPrivilegesWebView } from '../view/AccountRoleAndPrivilegesWebView';
import { AccountRoleAndPrivilegesData } from '../type';
import { AccountRoleAndPrivilegesDataProvider } from '../dataProvider/AccountRoleAndPrivilegesDataProvider';
import { CompanyData } from '../../types/CompanyData';

export class AccountRoleAndPrivilegesWebController extends BaseWebComponentController {
  public component: AccountRoleAndPrivilegesWebComponent;
  public view: AccountRoleAndPrivilegesWebView;
  public viewConstructor: typeof AccountRoleAndPrivilegesWebView;
  private dataProvider: AccountRoleAndPrivilegesDataProvider;

  constructor(component: AccountRoleAndPrivilegesWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: AccountRoleAndPrivilegesWebView,
      viewTemplate: templateAccountRoleAndPrivileges,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$roleDropdown,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onRoleChange,
      },
      {
        element: this.view.dom.$roleRights,
        event: 'change',
        callback: this.onRightsChange,
      },
    ];
  }

  private onRightsChange(event: WebComponentEvent): void {
    // If user manually change the rights:
    //    - switch to Custom Role
    //    - show error message if there is no right selected
    //    - dispatch event for other stuff (like: rebuild Select Targets' tree if is the case)
    if (event.target.id === 'manage-policies-checkbox') {
      this.view.dom.$managePoliciesReadRightCheckbox.checked = event.detail.checked;
      this.view.dom.$managePoliciesWriteRightCheckbox.checked = event.detail.checked;
    }
    if (event.target.id === 'manage-policies-read-checkbox' && !event.detail.checked) {
      this.view.dom.$managePoliciesWriteRightCheckbox.checked = false;
    }
    if (event.target.id === 'manage-policies-write-checkbox' && event.detail.checked) {
      this.view.dom.$managePoliciesReadRightCheckbox.checked = true;
    }
    const rightsWithOptions = this.view.enrichRightsWithOptions(this.component.getSelectedRights());
    this.view.toggleRightsCheckboxes(rightsWithOptions);
    this.view.selectCustomRole();
  }

  private onRoleChange(event: WebComponentEvent): void {
    // handle role selection: set the rights for selected role
    if (!event.detail.newValue.data) {
      return;
    }
    this.view.setRightsForRole(event.detail.newValue.data);
  }

  public async getDataForView(): Promise<AccountRoleAndPrivilegesData> {
    let viewData = this.component.getViewData() ?? {};
    if (viewData.companyInfo) {
      viewData.availableRoles = await this.getAvailableRoles(viewData.companyInfo._id);
    }
    return viewData;
  }

  public hasUserSelectedRightsOnlyForCompanies(): boolean {
    return (
      this.view.dom.$manageCompaniesRightCheckbox.checked &&
      !this.view.dom.$manageInventoryRightCheckbox.checked &&
      !this.view.dom.$manageUsersRightCheckbox.checked
    );
  }

  public hasUserSelectedRightsForNetwork(): boolean {
    return (
      this.view.dom.$manageInventoryRightCheckbox.checked ||
      this.view.dom.$managePoliciesRightCheckbox.checked ||
      this.view.dom.$manageReportsRightCheckbox.checked ||
      this.view.dom.$manageUsersRightCheckbox.checked
    );
  }

  private async getAvailableRoles(companyId: string) {
    if (!companyId) {
      return [];
    }

    if (!this.dataProvider) {
      this.dataProvider = new AccountRoleAndPrivilegesDataProvider();
    }

    return await this.dataProvider.getAvailableRolesForCompany(companyId);
  }

  public async updateRoleOptions(company: CompanyData) {
    const availableRoles = await this.getAvailableRoles(company._id);
    this.view.setupRoleDropdown(availableRoles);
  }
}
